import { createContext } from '@fluentui/react-context-selector';
import type { LoadingPhase } from '@ipfabric/ui-components/utils/loading/withLoadingIndicator';
import type { Point, Security } from '@ipfabric/utils';
import type { SecuritySchemaScope } from 'components/SecuritySchema/model/SecuritySchemaScope';
import type React from 'react';
import type {
  CommandModel,
  CommandModelCommandContext,
  CommandModelCommandDispatcher,
  CommandModelCommandFactory,
  CommandModelMergedState,
} from 'utils/hooks/useCommandModel';
import {
  type ContextSelectorsHook,
  useContextSelectors,
} from 'utils/react/useContextSelectors';
import type { ApiRequestFunction } from 'utils/request/useApi';

export interface SecuritySchemaBreadcrumb {
  page?: number;
  securityType: Security.SecurityType;
  ruleChainName: string;
  scrollPosition?: Point;
}

export interface SecuritySchemaState {
  breadcrumbs: SecuritySchemaBreadcrumb[];
  deviceSecurity: Security.DeviceSecurity | null;
  /**
   * key = rule chain name
   * value = index of rule to highlight (index === rules.length means default action)
   */
  highlightedRules: Record<string, number>;
  hostname: string | null;
  /** there is no initial state, the component cannot be mounted without a scope to display */
  loadingState: LoadingPhase;
  page: number;
  scope: SecuritySchemaScope | null;
}

export interface SecuritySchemaAdditionalCommandContext {
  apiRequest: ApiRequestFunction;
}

export interface SecuritySchemaOuterState {
  highlightedRuleLabel?: string;
}

type S = SecuritySchemaState;
type C = SecuritySchemaAdditionalCommandContext;
type O = SecuritySchemaOuterState;

export type SecuritySchemaCommandContext = CommandModelCommandContext<S, C, O>;
export type SecuritySchemaCommandFactory<TCommandArgs = never> =
  CommandModelCommandFactory<S, C, O, TCommandArgs>;
export type SecuritySchemaDispatch = CommandModelCommandDispatcher<S, C, O>;
export type SecuritySchemaMergedState = CommandModelMergedState<S, O>;
export type SecuritySchemaModel = CommandModel<S, C, O> & {
  treeScrollContainerRef: React.RefObject<HTMLDivElement>;
};

/**
 * No default value:
 * https://medium.com/@rivoltafilippo/typing-react-context-to-avoid-an-undefined-default-value-2c7c5a7d5947
 */
export const SecuritySchemaContext = createContext<SecuritySchemaModel>(
  undefined!,
);

export const useSecuritySchemaContextSelectors: ContextSelectorsHook<
  SecuritySchemaModel
> = (selectors) => useContextSelectors(SecuritySchemaContext, selectors);

export const initialSecuritySchemaState: SecuritySchemaState = {
  breadcrumbs: [],
  deviceSecurity: null,
  highlightedRules: {},
  hostname: null,
  loadingState: 'LOADING',
  page: 0,
  scope: null,
};

export const RULE_CHAIN_ITEMS_PER_PAGE = 10;
